import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  FolderOpen,
  FileSpreadsheet,
  Clock,
  Plus,
  ChevronDown,
} from "lucide-react";
import { useState } from "react";
import { useProjects } from "../hooks/useProjects";
import { useDashboardStats } from "../hooks/useDashboardStats";
import { useVariationsStats } from "../hooks/useVariationsStats";
import { dashboardLoadState } from "../hooks/dashboardLoadState";
import { DashboardLoadError } from "../components/DashboardLoadError";
import { formatCurrency, formatCompactCurrency, compactCurrencyTick } from "../lib/calculations";
import { DashboardInsight } from "../components/DashboardInsight";
import { ImportProjectsModal } from "../components/ImportProjectsModal";
import {
  TopProjectsChart,
  VariationsTrendChart,
  ClaimsTrendChart,
} from "../components/DashboardCharts";
import { ProjectHealthPanel } from "../components/ProjectHealthPanel";
import { PnlDashboardSection } from "../components/PnlDashboardSection";
import { DashboardPnlMonthOnMonth } from "../components/dashboard/DashboardPnlMonthOnMonth";
import { RetentionDashboardSection } from "../components/RetentionDashboardSection";
import { DashboardFilterProvider } from "../contexts/DashboardFilterContext";
import { DashboardFilterBar } from "../components/dashboard/DashboardFilterBar";
import { useDashboardFilteredData } from "../hooks/useDashboardFilteredData";

import { HowWorkedOut } from "../components/howWorkedOut/HowWorkedOut";
import { VARIATION_FIGURES } from "../hooks/useVariationsStats.metrics";
import { CLAIMS_FIGURES } from "../hooks/useDashboardFilteredData.metrics";

import { SkeletonLoader } from "../components/ui/SkeletonLoader";
import { TourEngine } from "../components/onboarding/TourEngine";
import { useTourManager } from "../hooks/useTourManager";

// Simple Chart for Top Projects by Claims (Progress)
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
function TopProjectsByClaimsChart({ data }: { data: any[] }) {
  const navigate = useNavigate();
  const chartData = [...data]
    .sort((a, b) => b.claimsToDate - a.claimsToDate)
    .slice(0, 5);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  const handleBarClick = (entry: any) => {
    if (entry?.projectId) {
      navigate(`/projects/${entry.projectId}`);
    }
  };

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={compactCurrencyTick} />
          <YAxis
            type="category"
            dataKey="projectName"
            width={100}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            formatter={(value: number | undefined) =>
              formatCurrency(value ?? 0)
            }
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
            wrapperStyle={{ zIndex: 100 }}
          />
          <Bar
            dataKey="claimsToDate"
            name="Total Claimed"
            fill="var(--color-primary)"
            radius={[0, 4, 4, 0]}
            barSize={24}
            cursor="pointer"
            onClick={(_data, index) => handleBarClick(chartData[index])}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Outer wrapper that provides the filter context
export function Dashboard() {
  const { projects, loading: projectsLoading, error: projectsError, refreshProjects } = useProjects();
  const { stats, loading: statsLoading, error: statsError, retry: retryStats } = useDashboardStats();
  const {
    stats: variationStats,
    loading: variationsLoading,
    error: variationsError,
    retry: retryVariations,
  } = useVariationsStats();
  const loadState = dashboardLoadState({
    projectsLoading,
    statsLoading,
    variationsLoading,
    projectsError: Boolean(projectsError),
    statsError,
    variationsError,
  });

  if (loadState === "loading") {
    return <SkeletonLoader variant="dashboard" />;
  }

  // SEQ-240: no figure, no AI insight inputs, when a read failed.
  if (loadState === "error") {
    return (
      <DashboardLoadError
        onRetry={() => {
          refreshProjects();
          retryStats();
          retryVariations();
        }}
      />
    );
  }

  return (
    <DashboardFilterProvider projects={projects}>
      <DashboardContent
        projects={projects}
        stats={stats}
        variationStats={variationStats}
        refreshProjects={refreshProjects}
      />
    </DashboardFilterProvider>
  );
}

// Inner component that consumes filter context
function DashboardContent({
  projects,
  stats,
  variationStats,
  refreshProjects,
}: {
  projects: ReturnType<typeof useProjects>['projects'];
  stats: ReturnType<typeof useDashboardStats>['stats'];
  variationStats: ReturnType<typeof useVariationsStats>['stats'];
  refreshProjects: () => void;
}) {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isNewProjectMenuOpen, setIsNewProjectMenuOpen] = useState(false);

  // ─── Onboarding Tour ──────────────────────────────────────────────────────
  const tour = useTourManager('dashboard');

  // Get filtered data from the centralized filter context
  const {
    filteredProjects,
    filteredVariationStats,
    filteredProjectStats,
    filteredMonthlyTrend,
    filteredContractValue,
    filteredPendingCount,
    filteredThisMonthClaims,
    filteredLastMonthClaims,
    filteredYearToDateClaims,
    filteredMonthlyBreakdown,
    isFiltered,
    totalProjectCount,
    datePreset,
    dateRange,
  } = useDashboardFilteredData(projects, variationStats, {
    rawClaimsProgress: stats.rawClaimsProgress,
    subcatToProject: stats.subcatToProject,
  });

  // Date filter label helpers
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const hasDateFilter = datePreset !== 'all_time';
  const dateFilterLabel = hasDateFilter
    ? dateRange.startMonth === dateRange.endMonth && dateRange.startYear === dateRange.endYear
      ? `${MONTH_NAMES[dateRange.startMonth - 1]} ${dateRange.startYear}`
      : `${MONTH_NAMES[dateRange.startMonth - 1]} ${dateRange.startYear} to ${MONTH_NAMES[dateRange.endMonth - 1]} ${dateRange.endYear}`
    : '';

  // Calculate Projects Over Limit Count based on FILTERED variationStats
  const projectsOverLimitCount = filteredProjectStats.filter((p) => {
    // contractValue is base only; need to add approvedVariations for revised contract
    const revisedContract = p.contractValue + p.approvedVariations;
    return p.claimsToDate > revisedContract;
  }).length;

  // SEQ-240: the same sums the cards below print, for the pop-ups' "Right now" rows.
  const totalClaimedToDate = filteredVariationStats.projectStats.reduce((sum, p) => sum + p.claimsToDate, 0);
  const revisedContractTotal = filteredContractValue + filteredVariationStats.totalApproved;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <h1 style={{ marginBottom: "var(--spacing-sm)" }}>
          Commercial Dashboard
        </h1>
        <p style={{ color: "var(--text-secondary)" }}>
          Overview of project financial health, claims progress, and risks.
          {isFiltered && (
            <span style={{ marginLeft: 8, fontSize: '0.82rem', color: 'var(--color-primary, #6366f1)', fontWeight: 500 }}>
              · Showing {filteredProjects.length} of {totalProjectCount} projects
            </span>
          )}
        </p>
      </div>

      {/* ─── Centralized Filter Bar ─── */}
      <DashboardFilterBar projects={projects} />

      {projects.length === 0 ? (
        <div 
          className="card"
          style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center",
            padding: "5rem 2rem",
            textAlign: "center",
            background: "linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)",
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 20px 40px -10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
            backdropFilter: "blur(12px)",
            marginTop: "2rem"
          }}
        >
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "var(--color-primary-light, #eff6ff)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.5rem",
            color: "var(--color-primary, #3b82f6)",
            boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.1)"
          }}>
            <FolderOpen size={40} strokeWidth={1.5} />
          </div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "var(--text-primary)", marginBottom: "0.5rem" }}>
            Welcome to your Dashboard
          </h2>
          <p style={{ color: "var(--text-secondary)", maxWidth: "450px", marginBottom: "2.5rem", lineHeight: 1.6 }}>
            You haven't set up any projects yet. Create your first project to start tracking financial health, managing claims, and monitoring variations.
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
            <Link to="/projects/new" className="btn btn-primary" style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.75rem 1.5rem" }}>
              <Plus size={18} />
              Create your first project
            </Link>
            <button 
              className="btn btn-secondary" 
              onClick={() => setIsImportModalOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.75rem 1.5rem", backgroundColor: "white" }}
            >
              <FileSpreadsheet size={18} />
              Import from CSV
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* AI Insights Section (New) */}
          <div style={{ marginBottom: "var(--spacing-xl)" }}>
            <DashboardInsight
              stats={filteredVariationStats}
              projectCount={filteredProjects.length}
            />
          </div>

      {/* DASHBOARD HEADER: Financial Overview (Redesigned) */}
      <div
        className="metric-grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "var(--spacing-lg)",
          marginBottom: "var(--spacing-xl)",
          display: "grid",
        }}
      >
        {/* CARD 1: Overall Claims Progress (Doughnut) */}
        <div
          className="card"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "var(--spacing-lg)",
            position: "relative",
            minHeight: "320px",
            overflow: "hidden",
          }}
        >
          <div className="hwo-heading" style={{ width: "100%", marginBottom: "8px" }}>
            <h3
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Overall Claims Progress
            </h3>
            <HowWorkedOut
              definition={VARIATION_FIGURES["claims-progress"]}
              rightNow={[
                { label: "Claimed", value: (totalClaimedToDate / (filteredContractValue || 1)) * 100, unit: "percent" },
                { label: "Total claimed", value: totalClaimedToDate, unit: "currency" },
                { label: "Original contract", value: filteredContractValue, unit: "currency" },
              ]}
            />
          </div>
          <div style={{ width: "100%", height: 200, position: "relative" }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "Claimed",
                      value: filteredVariationStats.projectStats.reduce(
                        (sum, p) => sum + p.claimsToDate,
                        0,
                      ),
                    },
                    {
                      name: "Remaining",
                      value: Math.max(
                        0,
                        filteredContractValue -
                          filteredVariationStats.projectStats.reduce(
                            (sum, p) => sum + p.claimsToDate,
                            0,
                          ),
                      ),
                    },
                  ]}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  <Cell fill="#3b82f6" /> {/* Claimed: Blue */}
                  <Cell fill="#f3f4f6" /> {/* Remaining: Grey */}
                </Pie>
                <Tooltip
                  formatter={(value: number | undefined) => [
                    formatCurrency(value ?? 0),
                    "Value",
                  ]}
                  contentStyle={{
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center Label */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#1f2937",
                }}
              >
                {(
                  (filteredVariationStats.projectStats.reduce(
                    (sum, p) => sum + p.claimsToDate,
                    0,
                  ) /
                    // Use ORIGINAL contract value (not Revised) for progress %
                    (filteredContractValue || 1)) *
                    100
                ).toFixed(2)}
                %
              </span>
              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                Claimed
              </span>
            </div>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "0.875rem",
              marginTop: "8px",
              padding: "0 8px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className="hwo-heading" style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>
                Total Claimed
                <HowWorkedOut
                  definition={VARIATION_FIGURES["claims-progress.total-claimed"]}
                  rightNow={[{ label: "Total claimed", value: totalClaimedToDate, unit: "currency" }]}
                />
              </span>
              <span data-seq-private="" style={{ fontWeight: 600, color: "#2563eb" }}>
                {formatCompactCurrency(
                  filteredVariationStats.projectStats.reduce(
                    (sum, p) => sum + p.claimsToDate,
                    0,
                  ),
                )}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                textAlign: "right",
              }}
            >
              <span
                className="hwo-heading"
                style={{ color: "var(--text-secondary)", fontSize: "0.75rem", alignSelf: "flex-end" }}
              >
                Total Revised
                <HowWorkedOut
                  definition={VARIATION_FIGURES["claims-progress.total-revised"]}
                  rightNow={[
                    { label: "Original contract", value: filteredContractValue, unit: "currency" },
                    { label: "Approved variations", value: filteredVariationStats.totalApproved, unit: "currency" },
                    { label: "Total revised", value: revisedContractTotal, unit: "currency" },
                  ]}
                />
              </span>
              <span data-seq-private="" style={{ fontWeight: 600, color: "#374151" }}>
                {formatCompactCurrency(
                  filteredContractValue + filteredVariationStats.totalApproved,
                )}
              </span>
            </div>
          </div>
        </div>

        {/* CARD 2: Contract Value Bridge */}
        <div
          className="card"
          style={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "320px",
          }}
        >
          <h3
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "24px",
            }}
          >
            Contract Value Bridge
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            {/* Original */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <div>
                <div
                  className="hwo-heading"
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-secondary)",
                    marginBottom: "4px",
                  }}
                >
                  Original Contract
                  <HowWorkedOut
                    definition={VARIATION_FIGURES["contract-bridge.original"]}
                    rightNow={[{ label: "Original contract", value: filteredContractValue, unit: "currency" }]}
                  />
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {formatCompactCurrency(filteredContractValue)}
                </div>
              </div>
              <div
                style={{
                  height: "1px",
                  backgroundColor: "#e5e7eb",
                  flex: 1,
                  margin: "0 16px 8px",
                }}
              ></div>
            </div>

            {/* Variations */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <div>
                <div
                  className="hwo-heading"
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--color-success-text)",
                    fontWeight: 500,
                    marginBottom: "4px",
                  }}
                >
                  + Approved Variations
                  <HowWorkedOut
                    definition={VARIATION_FIGURES["contract-bridge.approved"]}
                    rightNow={[{ label: "Approved variations", value: filteredVariationStats.totalApproved, unit: "currency" }]}
                  />
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 700,
                    color: "#059669",
                  }}
                >
                  {formatCompactCurrency(filteredVariationStats.totalApproved)}
                </div>
              </div>
              <div
                style={{
                  height: "1px",
                  backgroundColor: "#d1fae5",
                  flex: 1,
                  margin: "0 16px 8px",
                }}
              ></div>
            </div>

            {/* Revised */}
            <div
              style={{
                backgroundColor: "#eff6ff",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #dbeafe",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span className="hwo-heading" style={{ color: "var(--seq-blue-dark)", fontWeight: 500 }}>
                  Revised Total
                  <HowWorkedOut
                    definition={VARIATION_FIGURES["contract-bridge.revised"]}
                    rightNow={[{ label: "Revised total", value: revisedContractTotal, unit: "currency" }]}
                  />
                </span>
                <span data-seq-private=""
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#1e40af",
                  }}
                >
                  {formatCompactCurrency(
                    filteredContractValue + filteredVariationStats.totalApproved,
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CARD 3: Variations Status & Risk */}
        <div
          className="card"
          style={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "320px",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "24px",
              }}
            >
              <h3
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Variations Status
              </h3>
              {projectsOverLimitCount > 0 && (
                <span
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    fontSize: "0.75rem",
                    padding: "4px 8px",
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <AlertTriangle size={12} /> {projectsOverLimitCount} Critical
                  <HowWorkedOut
                    definition={VARIATION_FIGURES["variations.critical"]}
                    rightNow={[{ label: "Projects over their revised contract", value: projectsOverLimitCount, unit: "count" }]}
                  />
                </span>
              )}
            </div>

            {/* Pending (Blocked) */}
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#d97706",
                    fontWeight: 500,
                  }}
                >
                  <Clock size={16} /> Pending (Blocked)
                  <HowWorkedOut
                    definition={VARIATION_FIGURES["variations.pending"]}
                    rightNow={[
                      { label: "Pending", value: filteredVariationStats.totalPending, unit: "currency" },
                      { label: "Items waiting", value: filteredPendingCount, unit: "count" },
                    ]}
                  />
                </span>
                <span data-seq-private=""
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#d97706",
                  }}
                >
                  {formatCompactCurrency(filteredVariationStats.totalPending)}
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#fef3c7",
                  height: "8px",
                  borderRadius: "999px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#f59e0b",
                    height: "100%",
                    width: "100%",
                  }}
                ></div>{" "}
                {/* Indeterminate visual or full since it's the "Blocked" amount */}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#b45309",
                  marginTop: "8px",
                }}
              >
                Awaiting approval on {filteredPendingCount} items
              </div>
            </div>

            {/* Approved vs Credit */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginBottom: "8px",
                }}
              >
                <span className="hwo-heading" style={{ color: "var(--text-secondary)" }}>
                  Net Approved Impact
                  <HowWorkedOut
                    definition={VARIATION_FIGURES["variations.net-approved"]}
                    rightNow={[{ label: "Net approved impact", value: filteredVariationStats.totalApproved, unit: "currency" }]}
                  />
                </span>
                <span data-seq-private=""
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 700,
                    color:
                      filteredVariationStats.totalApproved >= 0 ? "#059669" : "#ef4444",
                  }}
                >
                  {formatCompactCurrency(filteredVariationStats.totalApproved)}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginTop: "8px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#f9fafb",
                    padding: "8px",
                    borderRadius: "4px",
                  }}
                >
                  <div className="hwo-heading">
                    Credits
                    <HowWorkedOut
                      definition={VARIATION_FIGURES["variations.credits"]}
                      rightNow={[{ label: "Credits", value: filteredVariationStats.totalCredit, unit: "currency" }]}
                    />
                  </div>
                  <div data-seq-private="" style={{ color: "#ef4444", fontWeight: 500 }}>
                    {formatCompactCurrency(filteredVariationStats.totalCredit)}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#f9fafb",
                    padding: "8px",
                    borderRadius: "4px",
                    textAlign: "right",
                  }}
                >
                  <div className="hwo-heading">
                    Additions
                    <HowWorkedOut
                      definition={VARIATION_FIGURES["variations.additions"]}
                      rightNow={[{ label: "Additions", value: filteredVariationStats.totalVariance, unit: "currency" }]}
                    />
                  </div>
                  <div data-seq-private="" style={{ color: "#059669", fontWeight: 500 }}>
                    {formatCompactCurrency(filteredVariationStats.totalVariance)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ─── Financial P&L Overview (role-protected internally) ─── */}
      <PnlDashboardSection />

      {/* Monthly Claims Summary (Progress Payments) */}
      <div data-tour-id="claims-summary" style={{ marginBottom: "var(--spacing-xl)" }}>
        <h3
          style={{
            fontSize: "1.1rem",
            marginBottom: "1rem",
            color: "var(--text-primary)",
          }}
        >
          {hasDateFilter ? `Claims Summary: ${dateFilterLabel}` : 'Monthly Claims Summary'}
        </h3>
        <div
          className="metric-grid"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {hasDateFilter ? (
            <>
              {/* When date filter is active, show period total + period breakdown */}
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    Total for Period
                    <HowWorkedOut
                      definition={CLAIMS_FIGURES["claims-summary.period-total"]}
                      rightNow={[{ label: "Claims total for the period", value: filteredMonthlyBreakdown.reduce((sum, m) => sum + m.amount, 0), unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(
                    filteredMonthlyBreakdown.reduce((sum, m) => sum + m.amount, 0)
                  )}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4 }}>
                  {dateFilterLabel}
                </div>
              </div>
              {/* Show each month in the range as separate cards (max 3) */}
              {filteredMonthlyBreakdown.slice(0, 2).map((m) => (
                <div key={m.monthName} className="card" style={{ padding: "1.5rem" }}>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--text-secondary)",
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    <span className="hwo-heading">
                      {m.monthName}
                      <HowWorkedOut
                        definition={CLAIMS_FIGURES["claims-summary.period-month"]}
                        rightNow={[{ label: "Claims for the month", value: m.amount, unit: "currency" }]}
                      />
                    </span>
                  </div>
                  <div data-seq-private=""
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                    }}
                  >
                    {formatCompactCurrency(m.amount)}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    This Month
                    <HowWorkedOut
                      definition={CLAIMS_FIGURES["claims-summary.this-month"]}
                      rightNow={[{ label: "Claims this month", value: filteredThisMonthClaims, unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(filteredThisMonthClaims)}
                </div>
              </div>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    Last Month
                    <HowWorkedOut
                      definition={CLAIMS_FIGURES["claims-summary.last-month"]}
                      rightNow={[{ label: "Claims last month", value: filteredLastMonthClaims, unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(filteredLastMonthClaims)}
                </div>
              </div>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    Year to Date ({new Date().getFullYear()})
                    <HowWorkedOut
                      definition={CLAIMS_FIGURES["claims-summary.year-to-date"]}
                      rightNow={[{ label: "Claims year to date", value: filteredYearToDateClaims, unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(filteredYearToDateClaims)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* SEQ-193: P&L month on month, only for viewers with financial access */}
      <DashboardPnlMonthOnMonth projects={projects} />

      {/* Monthly Variations Summary */}
      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <h3
          style={{
            fontSize: "1.1rem",
            marginBottom: "1rem",
            color: "var(--text-primary)",
          }}
        >
          {hasDateFilter ? `Variations Summary: ${dateFilterLabel}` : 'Monthly Variations Summary'}
        </h3>
        <div
          className="metric-grid"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {hasDateFilter ? (
            <>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    Total for Period
                    <HowWorkedOut
                      definition={VARIATION_FIGURES["variations-summary.period-total"]}
                      rightNow={[{ label: "Variations total for the period", value: filteredMonthlyTrend.reduce((sum, m) => sum + (m.submitted || 0), 0), unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(
                    filteredMonthlyTrend.reduce((sum, m) => sum + (m.submitted || 0), 0)
                  )}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4 }}>
                  {dateFilterLabel}
                </div>
              </div>
              {filteredMonthlyTrend.slice(0, 2).map((m) => (
                <div key={`${m.year}-${m.monthNum}`} className="card" style={{ padding: "1.5rem" }}>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--text-secondary)",
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    <span className="hwo-heading">
                      {m.month} {m.year}
                      <HowWorkedOut
                        definition={VARIATION_FIGURES["variations-summary.period-month"]}
                        rightNow={[{ label: "Variations for the month", value: m.submitted || 0, unit: "currency" }]}
                      />
                    </span>
                  </div>
                  <div data-seq-private=""
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                    }}
                  >
                    {formatCompactCurrency(m.submitted || 0)}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    This Month
                    <HowWorkedOut
                      definition={VARIATION_FIGURES["variations-summary.this-month"]}
                      rightNow={[{ label: "Variations this month", value: filteredVariationStats.thisMonthVariations, unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(filteredVariationStats.thisMonthVariations)}
                </div>
              </div>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    Last Month
                    <HowWorkedOut
                      definition={VARIATION_FIGURES["variations-summary.last-month"]}
                      rightNow={[{ label: "Variations last month", value: filteredVariationStats.lastMonthVariations, unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(filteredVariationStats.lastMonthVariations)}
                </div>
              </div>
              <div className="card" style={{ padding: "1.5rem" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <span className="hwo-heading">
                    Year to Date ({new Date().getFullYear()})
                    <HowWorkedOut
                      definition={VARIATION_FIGURES["variations-summary.year-to-date"]}
                      rightNow={[{ label: "Variations year to date", value: filteredVariationStats.yearToDateVariations, unit: "currency" }]}
                    />
                  </span>
                </div>
                <div data-seq-private=""
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {formatCompactCurrency(filteredVariationStats.yearToDateVariations)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* CHARTS ROW 1: Trends (Claims vs Variations) */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "var(--spacing-lg)",
          marginBottom: "var(--spacing-xl)",
        }}
      >
        {/* LEFT: Claims Trend */}
        <div className="card">
          <div className="card-header">
            <div className="hwo-heading">
              <h3 className="card-title">
                {hasDateFilter ? `Claims Trend: ${dateFilterLabel}` : 'Claims Trend (Last 12 Months)'}
              </h3>
              <HowWorkedOut definition={CLAIMS_FIGURES["claims-trend"]} />
            </div>
          </div>
          <div style={{ padding: "1rem" }}>
            <ClaimsTrendChart data={filteredMonthlyBreakdown} />
          </div>
        </div>

        {/* RIGHT: Variations Trend */}
        <div className="card">
          <div className="card-header">
            <div className="hwo-heading">
              <h3 className="card-title">Variations Trend (Last 12 Months)</h3>
              <HowWorkedOut definition={VARIATION_FIGURES["variations-trend"]} />
            </div>
          </div>
          <div style={{ padding: "1rem" }}>
            <VariationsTrendChart data={filteredMonthlyTrend} />
          </div>
        </div>
      </div>

      {/* CHARTS ROW 2: Top Projects (Claims vs Pending) */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "var(--spacing-lg)",
          marginBottom: "var(--spacing-xl)",
        }}
      >
        {/* LEFT: Top Projects by Claims */}
        <div className="card">
          <div className="card-header">
            <div className="hwo-heading">
              <h3 className="card-title">Top Projects by Progress (Claims)</h3>
              <HowWorkedOut definition={VARIATION_FIGURES["top-projects.progress"]} />
            </div>
          </div>
          <div style={{ padding: "1rem" }}>
            <TopProjectsByClaimsChart data={filteredProjectStats} />
          </div>
        </div>

        {/* RIGHT: Top Projects by Pending Variations */}
        <div className="card">
          <div className="card-header">
            <div className="hwo-heading">
              <h3 className="card-title">Top Projects by Pending Variations</h3>
              <HowWorkedOut definition={VARIATION_FIGURES["top-projects.pending"]} />
            </div>
          </div>
          <div style={{ padding: "1rem" }}>
            <TopProjectsChart data={filteredProjectStats} />
          </div>
        </div>
      </div>

      {/* ─── Retention Analytics (role-protected internally) ─── */}
      <RetentionDashboardSection />

      {/* Project Health Overview (Real Data Now) */}
      <div
        className="card"
        style={{
          marginBottom: "var(--spacing-xl)",
          marginTop: "var(--spacing-xl)",
        }}
      >
        <div className="card-header">
          <div className="hwo-heading">
            <h3 className="card-title">Project Health Overview</h3>
            <HowWorkedOut definition={VARIATION_FIGURES["project-health"]} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filteredProjects.map((project) => {
            // Find stats for this project from FILTERED stats
            const pStats = filteredProjectStats.find(
              (p) => p.projectId === project.id,
            );
            const claimed = pStats?.claimsToDate || 0;
            const pending = pStats?.pendingVariations || 0;
            const approved = pStats?.approvedVariations || 0;

            // Revised Contract = Base Contract + Approved Variations
            const baseContract =
              pStats?.contractValue || project.contractValue || 0;
            // No, strictly use pStats contractValue if available as it comes from calculation,
            // but wait, pStats.contractValue in `useVariationsStats` ALREADY sums up categories.
            // Does it include variations? NO. `useVariationsStats` line 169-176 sums categories.
            // So baseContract is correct.

            return (
              <ProjectHealthPanel
                key={project.id}
                projectName={project.job_location}
                contractValue={baseContract}
                approvedVariations={approved}
                pendingVariations={pending}
                totalClaimed={claimed}
                onClick={() =>
                  (window.location.href = `/projects/${project.id}`)
                }
              />
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: "var(--spacing-xl)" }}>
        <div className="card-header">
          <h3 className="card-title">Quick Actions</h3>
        </div>
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-md)",
            flexWrap: "wrap",
            position: "relative",
          }}
        >
          {/* New Project Dropdown */}
          <div style={{ position: "relative" }}>
            <button
              className="btn btn-primary"
              onClick={() => setIsNewProjectMenuOpen(!isNewProjectMenuOpen)}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <Plus size={18} />
              New Project
              <ChevronDown size={14} style={{ opacity: 0.7 }} />
            </button>

            {isNewProjectMenuOpen && (
              <>
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 40,
                  }}
                  onClick={() => setIsNewProjectMenuOpen(false)}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    marginTop: 4,
                    background: "var(--bg-card)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--border-radius-md)",
                    boxShadow: "var(--shadow-lg)",
                    padding: 4,
                    minWidth: 180,
                    zIndex: 50,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Link
                    to="/projects/new"
                    className="btn btn-ghost"
                    onClick={() => setIsNewProjectMenuOpen(false)}
                    style={{
                      justifyContent: "flex-start",
                      gap: 8,
                      padding: "8px 12px",
                      textDecoration: "none",
                    }}
                  >
                    <Plus size={16} style={{ opacity: 0.7 }} />
                    Create Manually
                  </Link>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setIsNewProjectMenuOpen(false);
                      setIsImportModalOpen(true);
                    }}
                    style={{
                      justifyContent: "flex-start",
                      gap: 8,
                      padding: "8px 12px",
                    }}
                  >
                    <FileSpreadsheet size={16} style={{ opacity: 0.7 }} />
                    Import from CSV
                  </button>
                </div>
              </>
            )}
          </div>

          <Link to="/projects" className="btn btn-secondary">
            <FolderOpen size={18} />
            View All Projects
          </Link>
        </div>
      </div>
      </>
      )}

      <ImportProjectsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          refreshProjects();
          window.location.reload(); // Refresh to update stats as well
        }}
      />

      {/* ─── Onboarding Tour Engine ─── */}
      {tour.activeTour && (
        <TourEngine
          tour={tour.activeTour}
          onComplete={tour.complete}
          onSkip={tour.skip}
        />
      )}
    </div>
  );
}
